import type { UnableReason } from '../../inspection';

// NY FROZEN LIMITATION TEMPLATES — DATA (docs/KNOWLEDGE-IMPORT-MANIFEST.md K-22).
//
// Source: docs/authority/ny/NY-INSPECTION-LIMITATIONS.md §5 (the frozen English + Chinese solar wording,
// verbatim, placeholders filled from the record) and §6 (the generic frozen template, instantiated for
// `obstructed` with its plain-words reason from §3 / NY-LIM-01 / NY-LIM-02). Owner decision OD-7 = C
// (2026-09-06, NY F1 Round 3): when an `obstructed` limitation carries no inspector note, the report
// reason is the applicable wording below — a genuine reason why observation was limited, never a bare
// "Not inspected". A typed note replaces it. This is report text: no code language, no engineering
// conclusion, no cost / life expectancy, no "appears fine behind …" (§6 constraints). Nothing here writes
// or decides an inspector result.
//
// NY F1 R3 narrow repair (F-R3-01): a reason code never establishes WHAT obstructed the view. The §5
// solar wording therefore applies ONLY when the inspector affirmatively identified a solar array — the
// record carries `templateId: 'solarArrayRoof'`. `obstructed` on its own — on a roof row as on any other,
// partial or whole, stored with `templateId: null` or not yet stored — resolves to the §6 generic wording.
// Nothing in this module, and nothing downstream of it, infers solar panels from the row being a roof row.
export type LimitationTemplateId = 'solarArrayRoof' | 'obstructedGeneric';

export const LIMITATION_TEMPLATE_IDS: readonly LimitationTemplateId[] = Object.freeze(['solarArrayRoof', 'obstructedGeneric']);

export function isLimitationTemplateId(value: unknown): value is LimitationTemplateId {
  return typeof value === 'string' && (LIMITATION_TEMPLATE_IDS as readonly string[]).includes(value);
}

// 'partial' = a limited portion of a row that keeps its observed result (OD-5 = A);
// 'whole'   = the whole row is Unable to Inspect (nothing of the component was observed).
export type LimitationScope = 'partial' | 'whole';

// The recorded roof observation method (descriptor #2, `roofObservationMethod`), used to fill the §5
// method placeholder. `null` = not recorded → the method clause is omitted, never invented.
export type RoofObservationMethod = 'walked' | 'ladderAtEave' | 'groundZoom' | 'drone';

export function isRoofObservationMethod(value: unknown): value is RoofObservationMethod {
  return value === 'walked' || value === 'ladderAtEave' || value === 'groundZoom' || value === 'drone';
}

// The frozen template a reason code carries ON ITS OWN (OD-7 = C): `obstructed` → the §6 generic
// wording; every other code → none (label + optional note, the R2 form). Never the solar wording — the
// row type is deliberately not an input (F-R3-01, header note).
export function limitationTemplateFor(reasonCode: UnableReason): LimitationTemplateId | null {
  return reasonCode === 'obstructed' ? 'obstructedGeneric' : null;
}

// Whether the quick reason sheet may OFFER the §5 solar wording as an explicit choice: only for a
// partially observed roof row (LIMITATIONS §3: "§5 for the partial roof-under-array case; §6 otherwise";
// the method row describes how the roof was observed and has no covering of its own). Offering is not
// selecting — the solar wording is used only once the inspector picks it.
export function solarTemplateOffered(rowTemplateId: string | null, scope: LimitationScope): boolean {
  return scope === 'partial' && rowTemplateId !== null && rowTemplateId.startsWith('roof-') && rowTemplateId !== 'roof-method';
}

// The template a STORED limitation prints with (the report, the item sheet's portion list): only
// `obstructed` has one; the inspector's explicit selection when the record carries a known template id,
// otherwise the generic wording. `null` — the generic state, and every record written without a
// selection — never becomes solar, whatever row it sits on.
export function resolveLimitationTemplate(reasonCode: UnableReason, storedTemplateId: string | null): LimitationTemplateId | null {
  if (reasonCode !== 'obstructed') return null;
  if (storedTemplateId !== null && isLimitationTemplateId(storedTemplateId)) return storedTemplateId;
  return 'obstructedGeneric';
}

export interface LimitationTemplateInput {
  locale: 'en-US' | 'zh-CN';
  // The component (the checklist row title) in the report language.
  component: string;
  // The portion not observed, in the inspector's words ('' = not named).
  extent: string;
  scope: LimitationScope;
  method: RoofObservationMethod | null;
}

// §5 bracket phrases, verbatim: [from the ground with a zoom lens / from a ladder at the eaves /
// by walking the accessible slopes / by drone] · [地面变焦 / 屋檐处梯子 / 步行可到达的坡面 / 无人机]
const METHOD_EN: Record<RoofObservationMethod, string> = Object.freeze({
  walked: 'by walking the accessible slopes',
  ladderAtEave: 'from a ladder at the eaves',
  groundZoom: 'from the ground with a zoom lens',
  drone: 'by drone'
});

const METHOD_ZH: Record<RoofObservationMethod, string> = Object.freeze({
  walked: '步行可到达的坡面',
  ladderAtEave: '屋檐处梯子',
  groundZoom: '地面变焦',
  drone: '无人机'
});

export function renderLimitationTemplate(id: LimitationTemplateId, input: LimitationTemplateInput): string {
  const extent = input.extent.trim();
  const component = input.component.trim();
  if (id === 'solarArrayRoof') {
    if (input.locale === 'zh-CN') {
      const area = extent.length > 0 ? extent : '部分屋面';
      const method = input.method ? `，观察方式为${METHOD_ZH[input.method]}` : '';
      return `太阳能板覆盖了${area}。太阳能阵列下方及其紧邻部位的屋面覆盖层与泛水不可见，未作检查。本次屋面检查仅限于可直接观察、可安全到达的屋面区域${method}。太阳能发电系统及其部件未作检查，不属于本次验屋范围。本限制说明不构成对被遮挡屋面状况的任何判断。`;
    }
    const area = extent.length > 0 ? extent : 'portions of the roof';
    const method = input.method ? `, which were observed ${METHOD_EN[input.method]}` : '';
    return `Solar panels cover ${area}. The roof covering and flashing beneath and immediately around the solar array were not visible and were not inspected. The roof inspection was limited to the readily visible and accessible roof areas${method}. The solar electrical system and its components were not inspected and are outside the scope of this home inspection. This limitation is not a statement about the condition of the concealed roofing.`;
  }
  // §6 generic template: [Component] [at / in location] was not [observed] because [reason in plain
  // words]. The inspection of [system] was limited to [what was observed]. No conclusion is made about
  // the condition of the [concealed] portion.
  if (input.locale === 'zh-CN') {
    const subject = input.scope === 'partial' ? `${component}（${extent.length > 0 ? extent : '部分'}）` : component;
    const limited = input.scope === 'partial' ? '本次对该部件的检查仅限于可直接观察、可到达的部分。' : '';
    const conclusion = input.scope === 'partial' ? '对被遮挡部分的状况不作任何结论。' : '对被遮挡部件的状况不作任何结论。';
    return `${subject}因被存放物品、家具、覆盖物或装修饰面遮挡而未能观察；验屋过程中不移动、不拆除遮挡物。${limited}${conclusion}`;
  }
  const subject = input.scope === 'partial' ? (extent.length > 0 ? `${component} (${extent})` : `Part of ${component}`) : component;
  const limited = input.scope === 'partial' ? ' The inspection of this component was limited to the portions that were readily visible and accessible.' : '';
  const conclusion = input.scope === 'partial' ? ' No conclusion is made about the condition of the concealed portion.' : ' No conclusion is made about the condition of the concealed component.';
  return `${subject} was not observed because it was obstructed by stored items, furnishings, coverings or finishes, which are not moved or removed during a home inspection.${limited}${conclusion}`;
}
